import React, { Component, CSSProperties, ErrorInfo, ReactNode } from "react";

interface Props {
  children?: ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    errorMessage: "Something went wrong. Please wait while we try to fix it."
  };

  public static getDerivedStateFromError(_: Error): Partial<State> {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const errorMessage = error?.message || (typeof error === "string" ? error : "");
    if (errorMessage.includes("Invalid state")) {
      const lastAttempt = sessionStorage.getItem('auth-reload-attempt');
      const now = Date.now();

      if (lastAttempt && (now - parseInt(lastAttempt, 10)) < 10000) { // 10-second threshold
        console.error("Caught 'Invalid state' error again within 10 seconds. Not reloading to prevent loop.", error, errorInfo);
        this.setState({
          errorMessage: "Authentication failed. Please try clearing your browser cache or contact support if the problem persists."
        });
      } else {
        console.error("Caught 'Invalid state' error, reloading the page to re-authenticate.", error, errorInfo);
        sessionStorage.setItem('auth-reload-attempt', now.toString());
        window.location.assign(window.location.origin);
      }
    } else {
      console.error("Uncaught error:", error, errorInfo);
      this.setState({
        errorMessage: "An unexpected error occurred. Please refresh the page."
      });
    }
  }

  public render() {
    if (this.state.hasError) {
      const containerStyle: CSSProperties = {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontSize: '1.5rem',
      };
      return <div style={containerStyle}>{this.state.errorMessage}</div>;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
